/**
 * screens/OnboardScreen.tsx — guided "Set up SK Voice" Setup Guide.
 * Mandatory-first onboarding: an accordion of required steps (one open at a
 * time, saving auto-opens the next), progress n/N, and a "Take SK Voice live"
 * card that only exists at 100%. Driven by the org's ENTRY STATE:
 *   - has a vertical (salon) → short path (business profile read from platform)
 *   - no vertical (Universal) → full path (captures business basics)
 * Phone number (Plivo subaccount/KYC/buy) is a later phase — shown as a step.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { useSession } from '../context/sessionContext';
import Icon from '../components/icon';
import { Card, Button, ScreenBody } from '../components/voiceUi';
import NumberProvisioningStep from '../components/NumberProvisioningStep';
import { getSettings, updateSettings, AgentSettings } from '../services/voice/voiceService';
import { patchOrgSetup } from '../services/auth/auth';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

type StepKey = 'profile' | 'number' | 'agent' | 'hours' | 'handling';
interface Step { key: StepKey; title: string; blurb: string }

const LANGUAGES: Array<[string, string]> = [
  ['en-IN', 'English'], ['ta-IN', 'Tamil'], ['hi-IN', 'Hindi'],
  ['te-IN', 'Telugu'], ['ml-IN', 'Malayalam'], ['kn-IN', 'Kannada'],
];

export default function OnboardScreen() {
  const { theme } = useTheme(); const c = theme.colors;
  const { tenant, go, orgId } = useConsole();
  const session = useSession();
  const hasVertical = !!tenant.vertical; // salon/hosp → business profile comes from the vertical

  // Universal orgs capture their business basics here; vertical orgs skip that step.
  const STEPS: Step[] = useMemo(() => {
    const base: Step[] = [
      { key: 'number', title: 'Phone number', blurb: 'Provision the number SK Voice answers on.' },
      { key: 'agent', title: 'Your agent', blurb: 'Name, language and voice — pre-filled, tweak if you like.' },
      { key: 'hours', title: 'Business hours', blurb: 'When you’re open. SK Voice handles calls outside them.' },
      { key: 'handling', title: 'After-hours & escalation', blurb: 'What happens when you’re closed or the caller needs a human.' },
    ];
    return hasVertical ? base : [{ key: 'profile', title: 'Business profile', blurb: 'Your business name, city and timezone.' }, ...base];
  }, [hasVertical]);

  const [done, setDone] = useState<Record<StepKey, boolean>>({} as Record<StepKey, boolean>);
  const [open, setOpen] = useState<StepKey | null>(null);
  const [loading, setLoading] = useState(true);

  // Form state (real fields wired to agent-settings; number/profile are captured
  // then persisted in the follow-up phases).
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [agentName, setAgentName] = useState('');
  const [lang, setLang] = useState('en-IN');
  const [transfer, setTransfer] = useState('');
  const [bizName, setBizName] = useState(tenant.name ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await getSettings();
      setSettings(data);
      setAgentName(data.persona?.name ?? '');
      if (data.persona?.language) setLang(data.persona.language);
      setTransfer(data.escalation?.transfer ?? '');
      // Rehydrate progress from the org's persisted setup so a reload resumes.
      const persisted = session.org?.setup?.steps ?? {};
      const doneMap = {} as Record<StepKey, boolean>;
      for (const s of STEPS) if (persisted[s.key] === 'done') doneMap[s.key] = true;
      setDone(doneMap);
      setLoading(false);
      setOpen(STEPS.find((s) => !doneMap[s.key])?.key ?? null);
    })();
  }, []); // eslint-disable-line

  const total = STEPS.length;
  const completed = STEPS.filter((s) => done[s.key]).length;
  const pct = Math.round((completed / total) * 100);

  // Mark a step done — persist to the org (survives reload) + open the next.
  const complete = (k: StepKey) => {
    const nextDone = { ...done, [k]: true };
    setDone(nextDone);
    const allDone = STEPS.every((s) => nextDone[s.key]);
    void patchOrgSetup({ step: { key: k, state: 'done' }, ...(allDone ? { completed: true } : {}) })
      .then(() => session.refresh())
      .catch(() => undefined);
    const idx = STEPS.findIndex((s) => s.key === k);
    const next = STEPS.slice(idx + 1).find((s) => !nextDone[s.key]);
    setOpen(next?.key ?? null);
  };

  const saveAgent = useCallback(async () => {
    setSaving(true);
    try {
      await updateSettings({ persona: { ...(settings?.persona ?? { name: '', voice: '', hold: '' }), name: agentName.trim(), language: lang } });
      showSuccess(null, 'Agent saved'); complete('agent');
    } catch (e) { handleApiError(e, 'Save failed'); } finally { setSaving(false); }
  }, [agentName, lang, settings]); // eslint-disable-line

  const saveHandling = useCallback(async () => {
    setSaving(true);
    try {
      await updateSettings({ escalation: { ...(settings?.escalation ?? { turnLimit: '', tier1: '', transfer: '' }), transfer: transfer.trim() } });
      showSuccess(null, 'Saved'); complete('handling');
    } catch (e) { handleApiError(e, 'Save failed'); } finally { setSaving(false); }
  }, [transfer, settings]); // eslint-disable-line

  if (loading) return <ScreenBody maxWidth={880}><View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={c.ink3} /></View></ScreenBody>;

  const input = { height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, color: c.ink, backgroundColor: c.card, fontSize: 13 } as const;
  const label = { fontSize: 12, color: c.ink2, marginBottom: 4, fontFamily: theme.font.sans } as const;

  return (
    <ScreenBody maxWidth={880}>
      {/* Header + progress */}
      <Text style={{ fontSize: 22, fontWeight: '600', letterSpacing: -0.44, color: c.ink, fontFamily: theme.font.sans }}>Set up SK Voice</Text>
      <Text style={{ fontSize: 12.5, color: c.ink3, marginTop: 2, marginBottom: 14, fontFamily: theme.font.sans }}>
        {hasVertical ? `Your ${tenant.vertical} setup is mostly done — just finish these.` : 'A few steps to get your AI receptionist live.'}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <View style={{ flex: 1, height: 8, backgroundColor: c.paper3, borderRadius: 20, overflow: 'hidden' }}>
          <View style={{ height: '100%', width: `${pct}%`, backgroundColor: c.ink, borderRadius: 20 }} />
        </View>
        <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink3 }}>{completed}/{total}</Text>
      </View>

      {/* Steps */}
      <View style={{ gap: 10 }}>
        {STEPS.map((s, i) => {
          const isOpen = open === s.key;
          const isDone = done[s.key];
          return (
            <Card key={s.key} style={{ overflow: 'hidden' }}>
              <Pressable onPress={() => setOpen(isOpen ? null : s.key)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 13 }}>
                <View style={{ width: 22, height: 22, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: isDone ? c.outcomeOk : c.paper3, borderWidth: isDone ? 0 : 1, borderColor: c.line }}>
                  {isDone ? <Icon name="check" size={12} color={c.paper} /> : <Text style={{ fontSize: 11, fontWeight: '600', color: c.ink3 }}>{i + 1}</Text>}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13.5, fontWeight: '600', color: c.ink, fontFamily: theme.font.sans }}>{s.title}</Text>
                  {!isOpen && <Text style={{ fontSize: 11.5, color: c.ink4, marginTop: 1, fontFamily: theme.font.sans }}>{s.blurb}</Text>}
                </View>
                <Icon name={isOpen ? 'chev-u' : 'chev-d'} size={14} color={c.ink4} />
              </Pressable>

              {isOpen && (
                <View style={{ paddingHorizontal: 16, paddingBottom: 16, gap: 10, borderTopWidth: 1, borderTopColor: c.line, paddingTop: 14 }}>
                  {s.key === 'profile' && (
                    <>
                      <View><Text style={label}>Business name</Text><TextInput value={bizName} onChangeText={setBizName} style={input} placeholderTextColor={c.ink4} /></View>
                      <Text style={{ fontSize: 11, color: c.ink4 }}>City & timezone come from your account location.</Text>
                      <Button kind="primary" label="Save & continue" onPress={() => complete('profile')} />
                    </>
                  )}
                  {s.key === 'number' && (
                    orgId
                      ? <NumberProvisioningStep orgId={orgId} onDone={() => complete('number')} />
                      : <Text style={{ fontSize: 12, color: c.ink4 }}>Sign in to provision a number.</Text>
                  )}
                  {s.key === 'agent' && (
                    <>
                      <View><Text style={label}>Agent name</Text><TextInput value={agentName} onChangeText={setAgentName} placeholder="e.g. your receptionist's name" placeholderTextColor={c.ink4} style={input} /></View>
                      <View>
                        <Text style={label}>Primary language</Text>
                        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                          {LANGUAGES.map(([v, l]) => (
                            <Pressable key={v} onPress={() => setLang(v)} style={{ paddingHorizontal: 11, height: 30, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: lang === v ? c.ink : c.card, borderWidth: lang === v ? 0 : 1, borderColor: c.line }}>
                              <Text style={{ fontSize: 12, fontWeight: '500', color: lang === v ? c.paper : c.ink2 }}>{l}</Text>
                            </Pressable>
                          ))}
                        </View>
                      </View>
                      <Button kind="primary" label={saving ? 'Saving…' : 'Save & continue'} onPress={saveAgent} disabled={saving || !agentName.trim()} />
                    </>
                  )}
                  {s.key === 'hours' && (
                    <>
                      <Text style={{ fontSize: 12.5, color: c.ink2, lineHeight: 18 }}>Your weekly opening hours live in your business profile (platform-owned) and every product reads them. {hasVertical ? 'Set them in your salon settings; shown here for reference.' : ''}</Text>
                      <Button kind="primary" label="Continue" onPress={() => complete('hours')} />
                    </>
                  )}
                  {s.key === 'handling' && (
                    <>
                      <View><Text style={label}>Transfer to (when a human is needed)</Text><TextInput value={transfer} onChangeText={setTransfer} placeholder="10-digit mobile" keyboardType="number-pad" placeholderTextColor={c.ink4} style={input} /></View>
                      <Text style={{ fontSize: 11, color: c.ink4 }}>When you’re closed or the agent can’t resolve, calls route here.</Text>
                      <Button kind="primary" label={saving ? 'Saving…' : 'Save & finish'} onPress={saveHandling} disabled={saving} />
                    </>
                  )}
                </View>
              )}
            </Card>
          );
        })}
      </View>

      {/* Take live — only at 100% */}
      {completed === total && (
        <View style={{ marginTop: 16, backgroundColor: c.ink, borderRadius: 12, padding: 18 }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: c.paper, marginBottom: 4, fontFamily: theme.font.sans }}>You’re ready.</Text>
          <Text style={{ fontSize: 12.5, color: c.paper, opacity: 0.75, marginBottom: 12, fontFamily: theme.font.sans }}>Everything’s set — take SK Voice live and start handling calls.</Text>
          <Pressable onPress={() => go('home')} style={{ alignSelf: 'flex-start', height: 34, paddingHorizontal: 15, borderRadius: 8, backgroundColor: c.paper, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 13, fontWeight: '600', color: c.ink }}>Take SK Voice live →</Text>
          </Pressable>
        </View>
      )}

      {/* Later, only if you need it */}
      <Text style={{ fontSize: 11.5, color: c.ink4, marginTop: 18, fontFamily: theme.font.sans }}>Later, only if you need it: Outbound calls · Campaigns.</Text>
    </ScreenBody>
  );
}
